/**
 * Phase 2L-A134 — Admin Partner Pricing Controls (partner-type matrix).
 *
 * Renders /admin/pricing/partner-types: one row per partner_type ×
 * package_key. Inline edit lets admins change `price_cents`,
 * `starts_at`, `ends_at`, `notes`, `active`. Audit history per row
 * opens in a side drawer.
 *
 * Restriction (per A134 spec): pws_franchisee is intentionally absent
 * — they keep their existing NATIONAL_PARTNER_PRICING in-code dict.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";
import { Building2, ArrowLeft, Plus, RotateCcw, History, Save, X, AlertTriangle } from "lucide-react";
import { formatDateAU } from "@/lib/formatDateAU";
import { toast } from "sonner";

const PACKAGE_LABELS = {
  basic: "AWARE 4-Point",
  tank:  "AWARE Tank 360",
  bore:  "AWARE Bore 360",
};

const PARTNER_TYPE_LABELS = {
  property_manager:        "Property Manager",
  conveyancer:             "Conveyancer",
  real_estate_agent:       "Real Estate Agent",
  buyer_agent:             "Buyer Agent",
  pump_shop:               "Pump Shop",
  plumber:                 "Plumber",
  building_inspector:      "Building Inspector",
  water_treatment_provider: "Water Treatment Provider",
};

export default function AdminPricingPartnerTypes() {
  const [data, setData] = useState({ items: [], eligible_partner_types: [], eligible_package_keys: [] });
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);   // {partner_type, package_key, price_cents, notes, starts_at}
  const [editing, setEditing] = useState(null); // pricing_id being edited
  const [auditFor, setAuditFor] = useState(null);  // {id, business_name}
  const [audit, setAudit] = useState([]);
  // Feb 2026 — bulk "Reset to retail" flow
  const [resetOpen, setResetOpen] = useState(false);
  const [resetIncludePartner, setResetIncludePartner] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetResult, setResetResult] = useState(null); // last diff report

  const load = () =>
    api.get("/admin/pricing/partner-types")
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  /** Build a quick lookup: rowsByCell[partner_type][package_key] = row */
  const rowsByCell = {};
  data.items.forEach((r) => {
    if (!rowsByCell[r.partner_type]) rowsByCell[r.partner_type] = {};
    rowsByCell[r.partner_type][r.package_key] = r;
  });

  const onSaveCreate = async () => {
    if (!draft) return;
    try {
      await api.post("/admin/pricing/partner-types", {
        partner_type: draft.partner_type,
        package_key: draft.package_key,
        price_cents: Number(draft.price_cents),
        notes: draft.notes || null,
      });
      toast.success(`Pricing added for ${PARTNER_TYPE_LABELS[draft.partner_type]} · ${PACKAGE_LABELS[draft.package_key]}`);
      setDraft(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save pricing row");
    }
  };

  const onSaveEdit = async (row, patch) => {
    try {
      await api.put(`/admin/pricing/${row.id}`, patch);
      toast.success("Pricing updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update pricing");
    }
  };

  const onDisable = async (row) => {
    if (!confirm(`Disable pricing for ${PARTNER_TYPE_LABELS[row.partner_type]} · ${PACKAGE_LABELS[row.package_key]}? Bookings will fall back to retail.`)) return;
    try {
      await api.delete(`/admin/pricing/${row.id}`);
      toast.success("Pricing disabled — bookings now fall back to retail.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not disable pricing");
    }
  };

  const openAudit = async (row) => {
    setAuditFor({ id: row.id, label: `${PARTNER_TYPE_LABELS[row.partner_type]} · ${PACKAGE_LABELS[row.package_key]}` });
    try {
      const { data } = await api.get(`/admin/pricing/${row.id}/audit`);
      setAudit(data.items || []);
    } catch (e) {
      toast.error("Could not load audit history");
    }
  };

  // Feb 2026 — Bulk reset to current retail prices.
  // POSTs `confirm=true` to the backend endpoint which returns a per-row
  // diff. We surface the diff inline so the admin has an immediate
  // audit receipt of exactly what changed.
  const onResetToRetail = async () => {
    setResetBusy(true);
    try {
      const { data } = await api.post("/admin/pricing/partner-types/reset-to-retail", {
        confirm: true,
        include_partner_overrides: resetIncludePartner,
      });
      setResetResult(data);
      if (data.reset_count > 0) {
        toast.success(`Reset ${data.reset_count} row${data.reset_count === 1 ? "" : "s"} to retail`);
      } else {
        toast.info("Nothing to reset — all active rows already match retail.");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally {
      setResetBusy(false);
    }
  };

  return (
    <PortalShell>
      <div data-testid="admin-pricing-partner-types-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/admin/partners" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-cyan-700 mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Partners
        </Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-600">Pricing controls</div>
            <h1 data-testid="admin-pricing-h1" className="font-display mt-2 text-3xl font-bold text-slate-900">
              Partner-type pricing
            </h1>
            <p className="text-slate-500 mt-1 text-sm">
              Defaults charged at booking time when no partner-specific override exists.
              PWS Franchisee national pricing is managed separately and is not shown here.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setResetResult(null); setResetOpen(true); }}
              data-testid="admin-pricing-reset-to-retail-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-sm font-semibold"
              title="Bulk reset all active partner-type prices to current retail"
            >
              <RotateCcw className="w-4 h-4" /> Reset to retail
            </button>
            <button
              onClick={() => setDraft({ partner_type: "property_manager", package_key: "tank", price_cents: 17900, notes: "" })}
              data-testid="admin-pricing-add-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold"
            >
              <Plus className="w-4 h-4" /> Add partner-type price
            </button>
          </div>
        </div>

        {loading && (
          <div className="mt-8 text-sm text-slate-500">Loading...</div>
        )}
        {!loading && data.items.length === 0 && (
          <div data-testid="admin-pricing-empty" className="mt-8 rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center">
            <div className="text-base font-display font-semibold text-slate-900">No partner-type pricing rows yet</div>
            <div className="mt-1 text-sm text-slate-500">All bookings will fall back to retail.</div>
          </div>
        )}

        {!loading && data.items.length > 0 && (
          <div className="mt-6 bg-white rounded-3xl border border-slate-200 overflow-hidden">
            <table data-testid="admin-pricing-table" className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[11px] uppercase tracking-wider font-bold text-slate-500">
                  <th className="px-5 py-3">Partner type</th>
                  <th className="px-5 py-3">Package</th>
                  <th className="px-5 py-3 text-right">Price</th>
                  <th className="px-5 py-3">Effective from</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Notes</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.items.map((row) => {
                  const isEditing = editing === row.id;
                  return (
                    <PricingRow
                      key={row.id}
                      row={row}
                      isEditing={isEditing}
                      onStartEdit={() => setEditing(row.id)}
                      onCancelEdit={() => setEditing(null)}
                      onSave={(patch) => onSaveEdit(row, patch)}
                      onDisable={() => onDisable(row)}
                      onAudit={() => openAudit(row)}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {draft && (
          <CreateDrawer
            draft={draft}
            eligibleTypes={data.eligible_partner_types}
            eligiblePackages={data.eligible_package_keys}
            onClose={() => setDraft(null)}
            onChange={(patch) => setDraft({ ...draft, ...patch })}
            onSave={onSaveCreate}
          />
        )}
        {auditFor && (
          <AuditDrawer
            label={auditFor.label}
            audit={audit}
            onClose={() => { setAuditFor(null); setAudit([]); }}
          />
        )}

        {resetOpen && (
          <ResetToRetailModal
            busy={resetBusy}
            result={resetResult}
            includePartnerOverrides={resetIncludePartner}
            onToggleIncludePartner={setResetIncludePartner}
            onCancel={() => { setResetOpen(false); setResetResult(null); }}
            onConfirm={onResetToRetail}
          />
        )}
      </div>
    </PortalShell>
  );
}

function PricingRow({ row, isEditing, onStartEdit, onCancelEdit, onSave, onDisable, onAudit }) {
  const [draftPrice, setDraftPrice] = useState(row.price_cents);
  const [draftNotes, setDraftNotes] = useState(row.notes || "");
  useEffect(() => {
    if (isEditing) { setDraftPrice(row.price_cents); setDraftNotes(row.notes || ""); }
  }, [isEditing, row]);
  return (
    <tr data-testid={`admin-pricing-row-${row.id}`} className={`hover:bg-slate-50/60 ${!row.active ? "opacity-50" : ""}`}>
      <td className="px-5 py-3 font-medium text-slate-900">{PARTNER_TYPE_LABELS[row.partner_type] || row.partner_type}</td>
      <td className="px-5 py-3 text-slate-700">{PACKAGE_LABELS[row.package_key] || row.package_key}</td>
      <td className="px-5 py-3 text-right tabular-nums font-semibold text-slate-900">
        {isEditing ? (
          <div className="inline-flex items-center gap-1">
            <span className="text-slate-500">$</span>
            <input
              type="number" min={0} step={1}
              value={Math.round(draftPrice / 100)}
              onChange={(e) => setDraftPrice(Math.max(0, Math.round(Number(e.target.value) * 100)))}
              data-testid={`admin-pricing-edit-price-${row.id}`}
              className="w-20 text-right border border-slate-300 rounded px-2 py-1"
            />
          </div>
        ) : (
          <>${(row.price_cents / 100).toFixed(0)}</>
        )}
      </td>
      <td className="px-5 py-3 text-slate-600 text-xs">{row.starts_at ? formatDateAU(row.starts_at) : "—"}</td>
      <td className="px-5 py-3">
        {row.active
          ? <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Active</span>
          : <span className="text-[11px] font-bold text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5">Disabled</span>}
      </td>
      <td className="px-5 py-3 text-slate-500 text-xs max-w-[260px] truncate">
        {isEditing ? (
          <input
            type="text" value={draftNotes}
            onChange={(e) => setDraftNotes(e.target.value)}
            data-testid={`admin-pricing-edit-notes-${row.id}`}
            className="w-full border border-slate-300 rounded px-2 py-1 text-xs"
            placeholder="Reason / context for this rate"
          />
        ) : (row.notes || "—")}
      </td>
      <td className="px-5 py-3 text-right whitespace-nowrap">
        {isEditing ? (
          <div className="inline-flex items-center gap-2">
            <button
              onClick={() => onSave({ price_cents: draftPrice, notes: draftNotes })}
              data-testid={`admin-pricing-save-${row.id}`}
              className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900"
            >
              <Save className="w-3 h-3" /> Save
            </button>
            <button onClick={onCancelEdit} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
              <X className="w-3 h-3" /> Cancel
            </button>
          </div>
        ) : (
          <div className="inline-flex items-center gap-3">
            <button
              onClick={onStartEdit}
              data-testid={`admin-pricing-edit-${row.id}`}
              className="text-xs font-semibold text-cyan-700 hover:text-cyan-900"
            >
              Edit
            </button>
            <button
              onClick={onAudit}
              data-testid={`admin-pricing-audit-${row.id}`}
              className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
            >
              <History className="w-3 h-3" /> History
            </button>
            {row.active && (
              <button
                onClick={onDisable}
                data-testid={`admin-pricing-disable-${row.id}`}
                className="text-xs text-rose-600 hover:text-rose-800"
              >
                Disable
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

function CreateDrawer({ draft, eligibleTypes, eligiblePackages, onClose, onChange, onSave }) {
  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div data-testid="admin-pricing-create-drawer" className="fixed right-0 top-0 bottom-0 w-full sm:w-[420px] bg-white z-50 shadow-2xl overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-bold text-slate-900">Add partner-type price</h2>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
          </div>
          <p className="mt-1 text-xs text-slate-500">Default partner-type rate. Single-partner overrides can be added on the partner&apos;s detail page.</p>
          <Field label="Partner type">
            <select
              value={draft.partner_type}
              onChange={(e) => onChange({ partner_type: e.target.value })}
              data-testid="admin-pricing-create-partner-type"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
            >
              {eligibleTypes.map((t) => (
                <option key={t} value={t}>{PARTNER_TYPE_LABELS[t] || t}</option>
              ))}
            </select>
          </Field>
          <Field label="Package">
            <select
              value={draft.package_key}
              onChange={(e) => onChange({ package_key: e.target.value })}
              data-testid="admin-pricing-create-package"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
            >
              {eligiblePackages.map((p) => (
                <option key={p} value={p}>{PACKAGE_LABELS[p] || p}</option>
              ))}
            </select>
          </Field>
          <Field label="Price (AUD)">
            <div className="inline-flex items-center gap-1 w-full">
              <span className="text-slate-500">$</span>
              <input
                type="number" min={0} step={1}
                value={Math.round((draft.price_cents || 0) / 100)}
                onChange={(e) => onChange({ price_cents: Math.max(0, Math.round(Number(e.target.value) * 100)) })}
                data-testid="admin-pricing-create-price"
                className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </Field>
          <Field label="Notes">
            <textarea
              value={draft.notes || ""}
              onChange={(e) => onChange({ notes: e.target.value })}
              data-testid="admin-pricing-create-notes"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm h-20 resize-none"
              placeholder="Why this rate? Free-text audit trail."
            />
          </Field>
          <button
            onClick={onSave}
            data-testid="admin-pricing-create-save"
            className="mt-4 w-full px-5 py-2.5 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold"
          >
            Save partner-type price
          </button>
          <div className="mt-3 text-[11px] text-slate-500 inline-flex items-start gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>This applies to NEW bookings only. Existing bookings keep the price they were created with.</span>
          </div>
        </div>
      </div>
    </>
  );
}

function AuditDrawer({ label, audit, onClose }) {
  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div data-testid="admin-pricing-audit-drawer" className="fixed right-0 top-0 bottom-0 w-full sm:w-[480px] bg-white z-50 shadow-2xl overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-bold text-slate-900">Audit history</h2>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
          </div>
          <div className="mt-1 text-xs text-slate-500">{label}</div>
          <ol className="mt-5 space-y-4">
            {audit.length === 0 && <li className="text-sm text-slate-500">No audit entries yet.</li>}
            {audit.map((e) => (
              <li key={e.id} className="text-xs leading-relaxed border-l-2 border-cyan-300 pl-3">
                <div className="text-[10px] uppercase font-bold text-cyan-700">{e.action}</div>
                <div className="text-slate-700 font-medium">{e.changed_by_email || e.changed_by_user_id}</div>
                <div className="text-slate-500">{formatDateAU(e.changed_at)}</div>
                {e.action === "update" && (
                  <div className="mt-1 text-slate-600">
                    {e.before?.price_cents !== e.after?.price_cents && (
                      <div>Price: ${(e.before?.price_cents || 0) / 100} → ${(e.after?.price_cents || 0) / 100}</div>
                    )}
                    {e.before?.notes !== e.after?.notes && <div>Notes updated</div>}
                  </div>
                )}
              </li>
            ))}
          </ol>
        </div>
      </div>
    </>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mt-4">
      <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

/**
 * ResetToRetailModal
 * ------------------
 * Two-step confirmation:
 *   1. Explain what will change + let admin choose scope
 *   2. Show the diff receipt the backend returned (list of rows reset)
 *
 * The modal never touches pricing itself — it just delegates to
 * `onConfirm` which POSTs to /api/admin/pricing/partner-types/reset-to-retail.
 * All destructive logic lives on the backend behind `confirm=true`.
 */
function ResetToRetailModal({ busy, result, includePartnerOverrides, onToggleIncludePartner, onCancel, onConfirm }) {
  const isResult = !!result;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4">
      <div
        data-testid="admin-pricing-reset-modal"
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[92vh] overflow-hidden flex flex-col"
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-start gap-3">
          <div className="mt-0.5 inline-flex items-center justify-center w-9 h-9 rounded-full bg-amber-100 text-amber-700">
            <AlertTriangle className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <div className="font-display text-lg font-semibold text-slate-900">
              {isResult ? "Reset complete" : "Reset partner-type pricing to retail"}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {isResult
                ? `${result.reset_count} row${result.reset_count === 1 ? "" : "s"} updated. Every change is in the audit log.`
                : "This will replace every ACTIVE partner-type price with the current retail price from the catalog."
              }
            </div>
          </div>
        </div>

        {!isResult && (
          <div className="px-5 py-4 space-y-3 text-sm text-slate-700 overflow-auto">
            <ul className="list-disc pl-5 space-y-1.5 text-[13px]">
              <li>Only <b>active</b> partner-type rows are affected. Disabled rows stay disabled.</li>
              <li>Rows already matching retail are skipped — no needless audit noise.</li>
              <li>Each changed row gets an audit entry (action: <code>reset</code>) so you can undo per-row from the History drawer.</li>
              <li>PWS Franchisee national pricing is <b>not</b> in this table and is <b>not</b> affected.</li>
            </ul>

            <label className="mt-3 flex items-start gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50 cursor-pointer">
              <input
                type="checkbox"
                data-testid="admin-pricing-reset-include-partner-checkbox"
                checked={includePartnerOverrides}
                onChange={(e) => onToggleIncludePartner(e.target.checked)}
                className="mt-0.5"
              />
              <span className="text-[12px] text-amber-900 leading-snug">
                <b>Also reset individual partner overrides.</b> Off by default — leave unchecked to protect bespoke partner deals.
              </span>
            </label>
          </div>
        )}

        {isResult && (
          <div className="px-5 py-4 overflow-auto">
            {result.reset_count === 0 && (
              <div className="text-sm text-slate-500 py-6 text-center">
                No rows needed resetting — everything already matches retail.
              </div>
            )}
            {result.reset_count > 0 && (
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2">Row</th>
                      <th className="text-right px-3 py-2">Was</th>
                      <th className="text-right px-3 py-2">Now</th>
                    </tr>
                  </thead>
                  <tbody data-testid="admin-pricing-reset-diff-body" className="divide-y divide-slate-100">
                    {result.diffs.map((d) => (
                      <tr key={d.id}>
                        <td className="px-3 py-2 text-slate-700">
                          {(PARTNER_TYPE_LABELS[d.partner_type] || d.partner_type)}
                          {" · "}
                          {(PACKAGE_LABELS[d.package_key] || d.package_key)}
                          {d.scope === "partner" && (
                            <span className="ml-1 text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 px-1 rounded">override</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-500 line-through">${(d.old_price_cents / 100).toFixed(0)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-700">${(d.new_price_cents / 100).toFixed(0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            data-testid="admin-pricing-reset-cancel-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-sm font-semibold"
          >
            <X className="w-4 h-4" /> {isResult ? "Close" : "Cancel"}
          </button>
          {!isResult && (
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              data-testid="admin-pricing-reset-confirm-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-amber-600 hover:bg-amber-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold"
            >
              <RotateCcw className="w-4 h-4" /> {busy ? "Resetting…" : "Confirm reset"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
